import React, { useState, useEffect, useRef } from "react";
import {
  collection,
  getDocs,
  getDoc,
  updateDoc,
  doc,
  query,
  orderBy,
  where,
  limit,
} from "firebase/firestore";
import {
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { db, auth, functions } from "./firebase";
import { fetchAllPlayers, getUserData, createUserData, updateUserIgn, fetchSeasonConfig } from "./utils/firebase";
import { CREW_PASS_REWARDS, IMPOSTOR_PASS_REWARDS } from "./constants/pass";
import { STOCKS } from "./constants/stocks";
import "./App.css";
import "./Mobile.css";

// Components
import LoginModal from "./components/LoginModal";
import Sidebar from "./components/Sidebar";
import MobileNav from "./components/MobileNav";
import LeaderboardPage from "./components/LeaderboardPage";
import LootboxPage from "./components/lootbox/LootboxPage";
import MarketplacePage from "./components/marketplace/MarketplacePage";
import StocksTab from "./components/marketplace/StocksTab";
import CasinoPage from "./components/casino/CasinoPage";

import StatsPage from "./components/StatsPage";
import AdminPage from "./components/AdminPage";
import DownloadPage from "./components/DownloadPage";
import SettingsPage from "./components/SettingsPage";
import PassPage from "./components/PassPage";
import ErrorBoundary from "./components/ErrorBoundary";
import { useToast } from './components/ToastContext';
import useMobile from './hooks/useMobile';

// ─────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────
export default function App() {
  const toast = useToast();
  const isMobile = useMobile();
  const [page, setPage] = useState(() => {
    // URL-based routing for direct links
    if (window.location.pathname === '/download') return 'settings';
    if (window.location.pathname === '/settings') return 'settings';
    return 'leaderboard';
  });
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState({ d: 0, h: 0, m: 0, s: 0, totalSec: Infinity });
  const TARGET_TIME = new Date('2026-04-16T01:00:00Z').getTime(); // 9 PM EST
  const alreadyExpired = Date.now() >= TARGET_TIME;
  const [gateExiting, setGateExiting] = useState(false);
  const [gateRemoved, setGateRemoved] = useState(alreadyExpired);
  const liveAudioRef = useRef(null);
  const ambientAudioRef = useRef(null);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const gateCanvasRef = useRef(null);
  const gateTextRevealedRef = useRef(false);
  const [gateTextRevealed, setGateTextRevealed] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [playerEmojis, setPlayerEmojis] = useState({}); // Map of playerId -> equipped emoji
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showIgnModal, setShowIgnModal] = useState(false);
  const [ignInput, setIgnInput] = useState('');
  const [seasonConfig, setSeasonConfig] = useState(null);
  const [tokenAdjustment, setTokenAdjustment] = useState({ crewmate: 0, imposter: 0, frozen: false });
  const tokenAdjustmentRef = useRef(tokenAdjustment);
  useEffect(() => { tokenAdjustmentRef.current = tokenAdjustment; }, [tokenAdjustment]);

  // ─── Gate audio & countdown (live) ───
  // Only for gated (non-allowed) users — dev accounts skip entirely
  const ALLOWED_EMAILS = ['test@test.com', 'fox_samuel@rocketmail.com'];
  const isAllowedEmail = currentUser && ALLOWED_EMAILS.includes(currentUser.email);
  // Wait until auth resolves before deciding whether to start ambient
  // Note: currentUser may be null (logged-out users still see the gate)
  const authResolved = !loading;
  const shouldRunLiveAudio = authResolved && !isAllowedEmail && !alreadyExpired;

  useEffect(() => {
    if (!shouldRunLiveAudio) return;

    const FADE_START = 300; // Ambient fade starts at 5:00 remaining
    const FADE_END = 230;   // Ambient silent at 3:50 remaining
    const REBORN_START = 225; // Reborn starts at 3:45 remaining
    let rebornStarted = false;

    // Calculate where we are right now
    const secNow = Math.floor(Math.max(0, TARGET_TIME - Date.now()) / 1000);

    // ── Create audio objects based on current time position ──
    // Ambient: only if we're before the silence point (3:50)
    if (secNow > FADE_END) {
      const a = new Audio('/The End Black Hole Loop (Fortnite Sound) - Sound Effect for editing.mp3');
      a.loop = true;
      // Set volume to correct fade level if we're already in the fade window
      if (secNow <= FADE_START) {
        a.volume = Math.max(0, 0.6 * ((secNow - FADE_END) / (FADE_START - FADE_END)));
      } else {
        a.volume = 0.6;
      }
      ambientAudioRef.current = a;
    }

    // Reborn: if we're already past 3:45, start it seeked to the right position
    let rebornSeeking = false;
    if (secNow <= REBORN_START && secNow > 0) {
      rebornStarted = true;
      rebornSeeking = true;
      const seekTo = REBORN_START - secNow;
      const a = new Audio('/Colin Stetson - Reborn - (From Hereditary Soundtrack) (SPOTISAVER).mp3');
      a.volume = 0.7;
      a.preload = 'auto';
      // Wait for metadata, seek, then play at correct position
      a.addEventListener('loadedmetadata', () => {
        a.currentTime = seekTo;
      }, { once: true });
      a.addEventListener('seeked', () => {
        rebornSeeking = false;
        a.play().then(() => {
          setAudioUnlocked(true);
          document.removeEventListener('click', tryPlay);
          document.removeEventListener('touchstart', tryPlay);
          document.removeEventListener('keydown', tryPlay);
        }).catch(() => {});
      }, { once: true });
      liveAudioRef.current = a;
    }

    // ── Unified play function: tries to play all pending audio ──
    // Also handles the silence window (3:50-3:45) where no audio exists yet
    const tryPlay = () => {
      const hasAudio = ambientAudioRef.current || liveAudioRef.current;
      if (!hasAudio) {
        // In silence window or before audio is created — just mark as unlocked
        // so the prompt disappears; browser interaction is now satisfied
        setAudioUnlocked(true);
        document.removeEventListener('click', tryPlay);
        document.removeEventListener('touchstart', tryPlay);
        document.removeEventListener('keydown', tryPlay);
        return;
      }
      if (ambientAudioRef.current && ambientAudioRef.current.paused) {
        ambientAudioRef.current.play().catch(() => {});
      }
      // Don't play Reborn if it's still seeking to the correct position
      if (liveAudioRef.current && liveAudioRef.current.paused && !rebornSeeking) {
        liveAudioRef.current.play().catch(() => {});
      }
      // Check if we successfully unlocked audio
      setTimeout(() => {
        const playing = (ambientAudioRef.current && !ambientAudioRef.current.paused) ||
                        (liveAudioRef.current && !liveAudioRef.current.paused);
        if (playing) {
          setAudioUnlocked(true);
          document.removeEventListener('click', tryPlay);
          document.removeEventListener('touchstart', tryPlay);
          document.removeEventListener('keydown', tryPlay);
        }
      }, 100);
    };
    tryPlay();
    document.addEventListener('click', tryPlay, { once: false });
    document.addEventListener('touchstart', tryPlay, { once: false });
    document.addEventListener('keydown', tryPlay, { once: false });

    // ── Smooth ambient fade-out (50ms interval) ──
    const ambientFadeId = setInterval(() => {
      if (!ambientAudioRef.current) { clearInterval(ambientFadeId); return; }
      const secLeft = Math.max(0, TARGET_TIME - Date.now()) / 1000;
      if (secLeft <= FADE_END) {
        ambientAudioRef.current.pause();
        ambientAudioRef.current = null;
        clearInterval(ambientFadeId);
      } else if (secLeft <= FADE_START) {
        ambientAudioRef.current.volume = Math.max(0, 0.6 * ((secLeft - FADE_END) / (FADE_START - FADE_END)));
      }
    }, 50);

    // ── Countdown timer (1s interval) ──
    let countdownId;
    const tick = () => {
      const diff = Math.max(0, TARGET_TIME - Date.now());
      const totalSec = Math.floor(diff / 1000);

      if (totalSec <= 0) {
        setCountdown({ d: 0, h: 0, m: 0, s: 0, totalSec: 0 });
        setGateExiting(true);
        // Fade out Reborn over 3.5s during exit
        if (liveAudioRef.current) {
          const a = liveAudioRef.current;
          const fadeStep = a.volume / 35;
          const rebornFadeId = setInterval(() => {
            if (a.volume > fadeStep) { a.volume -= fadeStep; } else { a.pause(); clearInterval(rebornFadeId); }
          }, 100);
        }
        // Clean up any remaining ambient
        if (ambientAudioRef.current) { ambientAudioRef.current.pause(); ambientAudioRef.current = null; }
        setTimeout(() => setGateRemoved(true), 4000);
        if (countdownId) clearInterval(countdownId);
        clearInterval(ambientFadeId);
        // Remove interaction listeners
        document.removeEventListener('click', tryPlay);
        document.removeEventListener('touchstart', tryPlay);
        document.removeEventListener('keydown', tryPlay);
        return;
      }

      // Start Reborn at 3:45 remaining (only if not already started)
      if (totalSec <= REBORN_START && !rebornStarted) {
        rebornStarted = true;
        const a = new Audio('/Colin Stetson - Reborn - (From Hereditary Soundtrack) (SPOTISAVER).mp3');
        a.volume = 0.7;
        a.play().catch(() => {});
        liveAudioRef.current = a;
      }

      setCountdown({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
        totalSec,
      });
    };
    tick();
    countdownId = setInterval(tick, 1000);

    // ── Cleanup ──
    return () => {
      clearInterval(ambientFadeId);
      clearInterval(countdownId);
      document.removeEventListener('click', tryPlay);
      document.removeEventListener('touchstart', tryPlay);
      document.removeEventListener('keydown', tryPlay);
      if (ambientAudioRef.current) { ambientAudioRef.current.pause(); ambientAudioRef.current = null; }
      if (liveAudioRef.current) { liveAudioRef.current.pause(); liveAudioRef.current = null; }
    };
  }, [shouldRunLiveAudio]); // eslint-disable-line react-hooks/exhaustive-deps

  // Separate countdown for dev accounts (no audio, just timer display)
  useEffect(() => {
    if (alreadyExpired) return;
    if (shouldRunLiveAudio) return; // Live effect handles it
    let id;
    const tick = () => {
      const diff = Math.max(0, TARGET_TIME - Date.now());
      const totalSec = Math.floor(diff / 1000);
      if (totalSec <= 0) {
        setCountdown({ d: 0, h: 0, m: 0, s: 0, totalSec: 0 });
        setGateRemoved(true);
        if (id) clearInterval(id);
        return;
      }
      setCountdown({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
        totalSec,
      });
    };
    tick();
    id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [shouldRunLiveAudio]); // eslint-disable-line react-hooks/exhaustive-deps
  const prevNotifIdsRef = useRef(new Set());

  // SECURITY: Disable console access in production
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      // Disable console methods
      const noop = () => {};
      Object.keys(console).forEach(key => {
        if (typeof console[key] === 'function') {
          console[key] = noop;
        }
      });

    }
  }, []);

  async function loadPlayers() {
    const all = await fetchAllPlayers();
    setPlayers(all);
    setLoading(false);
  }

  async function loadPlayerEmojis() {
    try {
      const usersSnapshot = await getDocs(collection(db, "users"));
      const emojiMap = {};
      usersSnapshot.docs.forEach(d => {
        const data = d.data();
        if (data.claimedPlayer && data.equippedEmoji) {
          // Store the full emoji object so we can access isShiny property
          emojiMap[data.claimedPlayer] = data.equippedEmoji;
          console.log(`Player ${data.claimedPlayer} has emoji:`, data.equippedEmoji);
        }
      });
      console.log("Player emojis loaded:", emojiMap);
      setPlayerEmojis(emojiMap);
    } catch (error) {
      console.error("Error loading player emojis:", error);
    }
  }

  async function loadUserData(user) {
    if (!user) {
      setUserData(null);
      return;
    }

    let data = await getUserData(user.uid);
    if (!data) {
      // Create user data if it doesn't exist
      data = await createUserData(user.uid, user.email);
    }

    setUserData(data);

    // Show IGN modal if user doesn't have an IGN set
    if (!data.ign) {
      setShowIgnModal(true);
    }

    // Check for weekly rewards
    await checkAndAwardWeeklyReward(user, data);
  }

  /**
   * Check if user is eligible for weekly reward and award based on rank.
   * All validation and token logic runs server-side via Cloud Function.
   */
  async function checkAndAwardWeeklyReward(user, userData) {
    if (!user || !userData || !userData.claimedPlayer) return;

    // Quick client-side check: can only claim once per week, resetting each Saturday 12:00 AM UTC
    const lastClaim = userData.lastWeeklyRewardClaim || userData.lastDailyRewardClaim || 0;
    const now = new Date();
    const dayOfWeek = now.getUTCDay(); // 0=Sun, 6=Sat
    const daysSinceSat = (dayOfWeek + 1) % 7;
    const lastSat = new Date(now);
    lastSat.setUTCDate(now.getUTCDate() - daysSinceSat);
    lastSat.setUTCHours(0, 0, 0, 0);
    if (lastClaim >= lastSat.getTime()) return; // already claimed this week

    try {
      const claimWeekly = httpsCallable(functions, 'claimWeeklyReward');
      await claimWeekly();
      // Reload data to reflect server-written tokens + notification
      await loadUserData(user);
      await loadNotifications(user.uid);
    } catch (err) {
      // 'failed-precondition' means already claimed — silently ignore
      if (!err.code || err.code !== 'functions/failed-precondition') {
        console.error("Error claiming weekly reward:", err);
      }
    }
  }

  async function loadNotifications(userId) {
    if (!userId) {
      console.log("loadNotifications: No userId provided");
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    console.log("loadNotifications: Loading notifications for user:", userId);

    try {
      const notificationsSnapshot = await getDocs(
        query(
          collection(db, "notifications"),
          where("userId", "==", userId),
          orderBy("timestamp", "desc"),
          limit(20)
        )
      );

      const notifs = notificationsSnapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }));

      console.log(`loadNotifications: Found ${notifs.length} notifications`, notifs);

      // Achievement notifications
      const currentIds = new Set(notifs.map(n => n.id));
      for (const notif of notifs) {
        if (notif.type === 'achievement' && !notif.read && !prevNotifIdsRef.current.has(notif.id)) {
          toast.success(notif.message);
        }
      }
      prevNotifIdsRef.current = currentIds;

      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.read).length);

      console.log(`loadNotifications: Set ${notifs.length} notifications, unread: ${notifs.filter(n => !n.read).length}`);
    } catch (error) {
      console.error("Error loading notifications:", error);
      console.error("Error details:", error.message, error.code);
    }
  }

  async function markNotificationRead(notificationId) {
    try {
      await updateDoc(doc(db, "notifications", notificationId), {
        read: true
      });
      await loadNotifications(currentUser.uid);
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  }

  async function clearAllNotifications() {
    try {
      const batch = notifications.map(notif =>
        updateDoc(doc(db, "notifications", notif.id), { read: true })
      );
      await Promise.all(batch);
      await loadNotifications(currentUser.uid);
    } catch (error) {
      console.error("Error clearing notifications:", error);
    }
  }

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          const checkAdminFn = httpsCallable(functions, 'checkAdmin');
          const result = await checkAdminFn();
          setIsAdmin(result.data.isAdmin);
        } catch (e) {
          console.error('Failed to check admin status:', e);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }

      // Load season config (non-blocking, independent of auth)
      fetchSeasonConfig().then(setSeasonConfig).catch(e => console.error('Failed to load season config:', e));

      if (user) {
        await loadPlayers();
        await loadUserData(user);
        loadPlayerEmojis();
        await loadNotifications(user.uid); // Load notifications
      } else {
        setUserData(null);
        setNotifications([]);
        setUnreadCount(0);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Auto-refresh: Reload players and user data periodically
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      if (currentUser) {
        loadPlayers();
        loadPlayerEmojis();
        // Skip user data refresh while roulette bets are active (frozen display)
        // to prevent server-deducted balance from conflicting with local bet tracking
        if (!tokenAdjustmentRef.current.frozen) {
          loadUserData(currentUser);
        }
        loadNotifications(currentUser.uid);
        // Refresh season config so S2 launch propagates to logged-in users
        fetchSeasonConfig().then(setSeasonConfig).catch(() => {});
      }
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(refreshInterval);
  }, [currentUser]);

  // ─── Desktop App Bridge ───
  // Expose overlay data for the Electron desktop app (polled via webview.executeJavaScript)
  useEffect(() => {
    if (!currentUser || !userData) {
      window.__AMONGLR_OVERLAY_DATA__ = null;
      return;
    }

    const claimedPlayer = userData.claimedPlayer;
    const player = claimedPlayer ? players.find(p => p.id === claimedPlayer) : null;

    // XP/Level data for overlay XP bar
    const xpTotal = userData.xp || 0;
    const userLevel = userData.level || 1;
    const xpNeeded = 100 + (userLevel - 1) * 50; // xpToNextLevel formula
    let xpUsed = 0;
    let lvl = 1;
    while (lvl < userLevel) { xpUsed += 100 + (lvl - 1) * 50; lvl++; }
    const currentLevelXp = xpTotal - xpUsed;

    window.__AMONGLR_OVERLAY_DATA__ = {
      loggedIn: true,
      uid: currentUser.uid,
      ign: userData.ign || userData.displayName || '',
      crewmateTokens: userData.crewmateTokens ?? 0,
      imposterTokens: userData.imposterTokens ?? 0,
      crewmateElo: player?.eloCrewmate ?? 0,
      imposterElo: player?.eloImposter ?? 0,
      claimedPlayer: claimedPlayer || null,
      level: userLevel,
      currentXp: currentLevelXp,
      xpToNext: xpNeeded,
    };
  }, [currentUser, userData, players]);

  // Expose Firestore query functions for the desktop app
  useEffect(() => {
    window.__AMONGLR_FETCH_POST_GAME__ = async (playerId) => {
      try {
        const cutoff = Date.now() - 120000;
        const gamesRef = collection(db, 'games');
        const q = query(gamesRef, where('timestamp', '>=', cutoff), orderBy('timestamp', 'desc'), limit(5));
        const snap = await getDocs(q);
        let found = null;
        snap.forEach(gameDoc => {
          if (found) return;
          const game = gameDoc.data();
          if (!game.eloChanges) return;
          const entry = game.eloChanges.find(e => e.id === playerId);
          if (entry) {
            found = { eloChanges: game.eloChanges, currentPlayerId: playerId, didWin: game.didWin, timestamp: game.timestamp, isWackyWednesday: game.eloMetadata?.isWackyWednesday || false };
          }
        });
        return found;
      } catch { return null; }
    };

    window.__AMONGLR_FETCH_TODAY_WL__ = async (playerId) => {
      try {
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        const gamesRef = collection(db, 'games');
        const q = query(gamesRef, where('timestamp', '>=', startOfDay), orderBy('timestamp', 'desc'));
        const snap = await getDocs(q);
        let wins = 0, losses = 0;
        snap.forEach(gameDoc => {
          const game = gameDoc.data();
          if (!game.eloChanges) return;
          const entry = game.eloChanges.find(e => e.id === playerId);
          if (!entry) return;
          if (entry.won) wins++;
          else losses++;
        });
        return { wins, losses };
      } catch { return { wins: 0, losses: 0 }; }
    };
  }, []);

  // Show login modal when navigating to admin page without auth
  useEffect(() => {
    if (page === "admin" && !isAdmin) {
      setShowLoginModal(true);
    }
  }, [page, isAdmin]);

  async function handleLogout() {
    await signOut(auth);
    setIsAdmin(false);
    setCurrentUser(null);
    setUserData(null);
    setPage("leaderboard");
  }

  async function handleSaveIgn() {
    if (!ignInput.trim()) {
      toast.warn("Please enter your Among Us IGN");
      return;
    }

    if (!currentUser) {
      toast.error("You must be logged in");
      return;
    }

    await updateUserIgn(currentUser.uid, ignInput.trim());
    setShowIgnModal(false);
    setIgnInput('');

    // Reload user data
    await loadUserData(currentUser);
    toast.success(`IGN saved: ${ignInput.trim()}\n\nMake sure this EXACTLY matches your Among Us name (case-sensitive)!`);
  }

  // Check if user has claimable pass rewards (for red dot on Pass tab)
  const hasClaimablePassRewards = (() => {
    if (!userData) return false;
    const claimed = userData.passRewardsClaimed || {};
    // Check crew pass
    if (userData.crewPassOwned) {
      const crewTier = userData.crewPassTier || 0;
      for (const entry of CREW_PASS_REWARDS) {
        if (entry.tier <= crewTier && !claimed[`crew_${entry.tier}`]) return true;
      }
    }
    // Check impostor pass
    if (userData.impostorPassOwned) {
      const impTier = userData.impostorPassTier || 0;
      for (const entry of IMPOSTOR_PASS_REWARDS) {
        if (entry.tier <= impTier && !claimed[`impostor_${entry.tier}`]) return true;
      }
    }
    return false;
  })();

  // ─── Live stock portfolio value (for accurate net worth in nav) ───
  const [liveStockValue, setLiveStockValue] = useState(null);

  useEffect(() => {
    if (!currentUser) { setLiveStockValue(null); return; }

    async function fetchLiveStockValue() {
      try {
        const [priceSnap, holdingsSnap] = await Promise.all([
          getDoc(doc(db, 'stockPrices', 'current')),
          getDoc(doc(db, 'stockHoldings', currentUser.uid)),
        ]);
        const prices = priceSnap.exists() ? priceSnap.data().prices || {} : {};
        const holdings = holdingsSnap.exists() ? holdingsSnap.data().holdings || {} : {};
        let value = 0;
        for (const [stockId, holding] of Object.entries(holdings)) {
          if (holding.shares > 0) {
            const stockDef = STOCKS.find(s => s.id === stockId);
            if (stockDef && prices[stockDef.ticker]) {
              value += holding.shares * prices[stockDef.ticker].price;
            }
          }
        }
        setLiveStockValue(parseFloat(value.toFixed(4)));
      } catch (err) {
        console.warn('Failed to fetch live stock value:', err.message);
      }
    }

    fetchLiveStockValue();
    const interval = setInterval(fetchLiveStockValue, 60000);
    return () => clearInterval(interval);
  }, [currentUser]);

  // ─── Maintenance Gate ───
  const isAllowed = isAllowedEmail;

  // Determine the countdown state to render (preview or live)
  // (computed before hooks so hooks can reference these values)
  const renderCountdown = !loading && !isAllowed && !gateRemoved;
  const displayCountdown = countdown;
  const isHeartbeat = displayCountdown.totalSec <= 30 && displayCountdown.totalSec > 0;

  // Track text reveal — trigger once when gate first renders
  useEffect(() => {
    if (renderCountdown && !gateTextRevealedRef.current) {
      gateTextRevealedRef.current = true;
      setTimeout(() => setGateTextRevealed(true), 50);
    }
    if (!renderCountdown) {
      gateTextRevealedRef.current = false;
      setGateTextRevealed(false);
    }
  }, [renderCountdown]);

  // Ref to pass current totalSec into particle animation without re-creating it
  const gateTotalSecRef = useRef(displayCountdown.totalSec);
  gateTotalSecRef.current = displayCountdown.totalSec;

  // Particle canvas effect — only re-runs when gate appears/disappears
  useEffect(() => {
    if (!renderCountdown) return;
    const canvas = gateCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId;
    const particles = [];
    const PARTICLE_COUNT = 120;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Initialize particles
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 1.5 + 0.5,
        baseSpeed: Math.random() * 0.3 + 0.1,
        opacity: Math.random() * 0.5 + 0.1,
        angle: Math.random() * Math.PI * 2,
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      // Read current intensity from ref (updated every render)
      const secLeft = gateTotalSecRef.current;
      const intens = secLeft <= 225 ? 1 - (secLeft / 225) : 0;
      // Wormhole pull strength: 0 when calm, accelerates toward center as intensity grows
      const pullStrength = intens * intens * 2.5; // quadratic ramp
      const baseAlpha = 0.3 + intens * 0.5;

      for (const p of particles) {
        // Vector from particle to center
        const dx = cx - p.x;
        const dy = cy - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const maxDist = Math.sqrt(cx * cx + cy * cy);

        // Move toward center with pull, plus gentle drift
        p.x += (dx / dist) * pullStrength * (1 + p.baseSpeed) + Math.cos(p.angle) * p.baseSpeed * 0.5;
        p.y += (dy / dist) * pullStrength * (1 + p.baseSpeed) + Math.sin(p.angle) * p.baseSpeed * 0.5;
        p.angle += 0.002;

        // Respawn at edges if too close to center or out of bounds
        if (dist < 8 + intens * 30 || p.x < -10 || p.x > canvas.width + 10 || p.y < -10 || p.y > canvas.height + 10) {
          const edge = Math.floor(Math.random() * 4);
          if (edge === 0) { p.x = 0; p.y = Math.random() * canvas.height; }
          else if (edge === 1) { p.x = canvas.width; p.y = Math.random() * canvas.height; }
          else if (edge === 2) { p.x = Math.random() * canvas.width; p.y = 0; }
          else { p.x = Math.random() * canvas.width; p.y = canvas.height; }
          p.angle = Math.random() * Math.PI * 2;
        }

        // Draw — redder and brighter as intensity grows
        const distRatio = dist / maxDist;
        const r = Math.floor(150 + intens * 82);
        const g = Math.floor(56 * (1 - intens * 0.6));
        const b = Math.floor(74 * (1 - intens * 0.5));
        const alpha = p.opacity * baseAlpha * (0.3 + distRatio * 0.7);
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * (1 + intens * 0.8), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
        ctx.fill();

        // Draw subtle trail toward center when pulling
        if (pullStrength > 0.3) {
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x + (dx / dist) * Math.min(dist * 0.08, 20) * intens, p.y + (dy / dist) * Math.min(dist * 0.08, 20) * intens);
          ctx.strokeStyle = `rgba(${r},${g},${b},${alpha * 0.3})`;
          ctx.lineWidth = p.size * 0.5;
          ctx.stroke();
        }
      }

      animId = requestAnimationFrame(animate);
    };
    animId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, [renderCountdown]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner">🔴</div>
        <div className="loading-text">Loading...</div>
      </div>
    );
  }

  const isIntense = displayCountdown.totalSec <= 225; // last 3:45
  const isUltra = displayCountdown.totalSec <= 60;    // last 60s
  const isFinal = displayCountdown.totalSec <= 10;    // last 10s
  const isExiting = gateExiting;

  if (renderCountdown) {
    // intensity progress: 0 at 225s, 1 at 0s
    const intensity = isIntense ? 1 - (displayCountdown.totalSec / 225) : 0;
    // scale timer: normal 1x, grows to 1.8x at zero
    const timerScale = 1 + (isIntense ? intensity * 0.8 : 0);
    // glow intensity
    const glowStrength = isIntense ? Math.floor(intensity * 60) : 8;
    // background pulse speed
    const pulseSpeed = isFinal ? 0.3 : isUltra ? 0.8 : isIntense ? 1.5 : 3;
    // screen shake for final 10s (disabled during exit so scale-up transition plays)
    const shakeClass = isFinal && !isExiting ? 'gate-shake' : '';
    // hide days/hours when in intense mode and they're zero
    const showDays = displayCountdown.d > 0;
    const showHours = displayCountdown.d > 0 || displayCountdown.h > 0;
    const timerParts = [
      ...(showDays ? [{ val: displayCountdown.d, label: 'DAYS' }] : []),
      ...(showHours ? [{ val: displayCountdown.h, label: 'HRS' }] : []),
      { val: displayCountdown.m, label: 'MIN' },
      { val: displayCountdown.s, label: 'SEC' },
    ];
    // Background color shift: #050508 → deep crimson
    const bgR = Math.floor(5 + intensity * 30);
    const bgG = Math.floor(5 - intensity * 3);
    const bgB = Math.floor(8 + intensity * 10);
    // Heartbeat animation class
    const heartbeatClass = isHeartbeat ? 'gate-heartbeat' : '';
    // "SEASON 2" letter reveal
    const titleLetters = 'SEASON 2'.split('');

    return (
      <div className={`${shakeClass} ${heartbeatClass}`} style={{
        minHeight: '100vh',
        background: `rgb(${bgR},${bgG},${bgB})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Orbitron', sans-serif",
        overflow: 'hidden',
        position: 'relative',
        opacity: isExiting ? 0 : 1,
        transform: isExiting ? 'scale(1.3)' : 'scale(1)',
        transition: isExiting ? 'opacity 3.5s ease, transform 3.5s ease' : 'background 2s ease',
      }}>
        {/* Particle canvas */}
        <canvas
          ref={gateCanvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            pointerEvents: 'none',
          }}
        />
        {/* Radial background glow - intensifies */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse at 50% 50%, rgba(232,56,74,${0.08 + intensity * 0.25}) 0%, transparent 70%)`,
          pointerEvents: 'none',
          transition: 'background 1s ease',
        }} />
        {/* Pulsing ring - always rendered, fades in smoothly */}
        <div style={{
          position: 'absolute',
          width: `${300 + intensity * 400}px`,
          height: `${300 + intensity * 400}px`,
          borderRadius: '50%',
          border: `2px solid rgba(232,56,74,${isIntense ? 0.1 + intensity * 0.4 : 0})`,
          animation: isIntense ? `gate-ring-pulse ${pulseSpeed}s ease-in-out infinite` : 'none',
          pointerEvents: 'none',
          transition: 'width 1s ease, height 1s ease, border 1s ease',
        }} />
        {/* Second ring - fades in at ultra */}
        <div style={{
          position: 'absolute',
          width: `${200 + intensity * 300}px`,
          height: `${200 + intensity * 300}px`,
          borderRadius: '50%',
          border: `1px solid rgba(232,56,74,${isUltra ? intensity * 0.3 : 0})`,
          animation: isUltra ? `gate-ring-pulse ${pulseSpeed * 0.7}s ease-in-out infinite reverse` : 'none',
          pointerEvents: 'none',
          transition: 'width 1s ease, height 1s ease, border 1s ease',
        }} />
        {/* Light beams - fade in at final */}
        <div style={{
          position: 'absolute',
          width: '2px',
          height: '100vh',
          background: `linear-gradient(transparent, rgba(232,56,74,${isFinal ? intensity * 0.8 : 0}), transparent)`,
          left: '50%',
          animation: isFinal ? 'gate-beam 0.5s ease-in-out infinite alternate' : 'none',
          pointerEvents: 'none',
          transition: 'background 1s ease',
        }} />
        <div style={{
          position: 'absolute',
          width: '100vw',
          height: '2px',
          background: `linear-gradient(transparent, rgba(232,56,74,${isFinal ? intensity * 0.6 : 0}), transparent)`,
          top: '50%',
          animation: isFinal ? 'gate-beam 0.7s ease-in-out infinite alternate' : 'none',
          pointerEvents: 'none',
          transition: 'background 1s ease',
        }} />

        <div style={{
          textAlign: 'center',
          zIndex: 1,
          padding: '40px',
          maxWidth: '800px',
          transform: `scale(${timerScale})`,
          transition: 'transform 1s ease',
        }}>
          {/* Letter-by-letter title reveal */}
          <h1 style={{
            fontSize: 'clamp(24px, 5vw, 48px)',
            fontWeight: 900,
            color: isFinal ? '#fff' : 'var(--red)',
            textTransform: 'uppercase',
            letterSpacing: isIntense ? `${3 + intensity * 8}px` : '3px',
            lineHeight: 1.2,
            marginBottom: isIntense ? '24px' : '16px',
            textShadow: `0 0 ${glowStrength}px rgba(232,56,74,${0.4 + intensity * 0.6})`,
            transition: 'letter-spacing 1s ease, color 1s ease, text-shadow 1s ease, margin-bottom 1s ease',
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'nowrap',
          }}>
            {titleLetters.map((ch, i) => (
              <span key={i} style={{
                display: 'inline-block',
                opacity: gateTextRevealed ? 1 : 0,
                transform: gateTextRevealed ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.5)',
                transition: `opacity 0.4s ease ${i * 0.07}s, transform 0.4s ease ${i * 0.07}s`,
                width: ch === ' ' ? '0.3em' : 'auto',
              }}>
                {ch === ' ' ? '\u00A0' : ch}
              </span>
            ))}
          </h1>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: isIntense ? 'clamp(16px, 4vw, 32px)' : 'clamp(12px, 3vw, 24px)',
            marginBottom: '32px',
            transition: 'gap 1s ease',
          }}>
            {timerParts.map(({ val, label }) => (
              <div key={label} style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}>
                <div style={{
                  fontSize: 'clamp(32px, 7vw, 64px)',
                  fontWeight: 900,
                  color: isFinal ? '#fff' : isUltra ? 'var(--red)' : 'var(--text-primary)',
                  lineHeight: 1,
                  minWidth: 'clamp(48px, 10vw, 90px)',
                  textAlign: 'center',
                  textShadow: `0 0 ${glowStrength}px rgba(232,56,74,${0.3 + intensity * 0.7})`,
                  transition: 'color 1s ease, text-shadow 1s ease',
                }}>
                  {String(val).padStart(2, '0')}
                </div>
                <div style={{
                  fontSize: 'clamp(10px, 1.5vw, 14px)',
                  color: isIntense ? `rgba(232,56,74,${0.5 + intensity * 0.5})` : 'var(--text-secondary)',
                  letterSpacing: '2px',
                  marginTop: '8px',
                  fontFamily: "'Rajdhani', sans-serif",
                  transition: 'color 1s ease',
                }}>
                  {label}
                </div>
              </div>
            ))}
          </div>
          {/* Subtle prompt to unlock audio - only for gated players with live audio */}
          {shouldRunLiveAudio && !audioUnlocked && !isExiting && (
            <div style={{
              marginTop: '32px',
              fontSize: '12px',
              color: 'var(--text-secondary)',
              fontFamily: "'Rajdhani', sans-serif",
              letterSpacing: '2px',
              opacity: 0.4,
              animation: 'gate-fade-in 3s ease forwards',
            }}>
              TAP ANYWHERE FOR SOUND
            </div>
          )}
        </div>

        <style>{`
          @keyframes gate-fade-in {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
          @keyframes gate-ring-pulse {
            0%, 100% { transform: scale(1); opacity: 0.3; }
            50% { transform: scale(1.15); opacity: 1; }
          }
          @keyframes gate-beam {
            0% { opacity: 0.3; }
            100% { opacity: 1; }
          }
          @keyframes gate-shake {
            0%, 100% { transform: translate(0, 0); }
            10% { transform: translate(-2px, 1px); }
            20% { transform: translate(2px, -1px); }
            30% { transform: translate(-1px, 2px); }
            40% { transform: translate(1px, -2px); }
            50% { transform: translate(-2px, 0px); }
            60% { transform: translate(2px, 1px); }
            70% { transform: translate(-1px, -1px); }
            80% { transform: translate(1px, 2px); }
            90% { transform: translate(0px, -2px); }
          }
          .gate-shake {
            animation: gate-shake 0.15s infinite;
          }
          @keyframes gate-heartbeat {
            0% { transform: scale(1); }
            7% { transform: scale(1.015); }
            14% { transform: scale(1); }
            21% { transform: scale(1.02); }
            28% { transform: scale(1); }
            100% { transform: scale(1); }
          }
          .gate-heartbeat {
            animation: gate-heartbeat 1.2s ease-in-out infinite;
          }
          .gate-shake.gate-heartbeat {
            animation: gate-shake 0.15s infinite;
          }
        `}</style>
      </div>
    );
  }

  const shellProps = {
    page, setPage, isAdmin,
    user: currentUser, userData, tokenAdjustment,
    onLogout: handleLogout,
    onChangeIgn: () => {
      setIgnInput(userData?.ign || '');
      setShowIgnModal(true);
    },
    unreadCount, notifications, showNotifications, setShowNotifications,
    onMarkRead: markNotificationRead, onClearAll: clearAllNotifications,
    seasonConfig, hasClaimablePassRewards, liveStockValue,
  };

  const pageContent = (
    <ErrorBoundary>
      {page === "leaderboard" && <LeaderboardPage players={players} onPlayersChange={loadPlayers} user={currentUser} userData={userData} onUserDataChange={() => loadUserData(currentUser)} playerEmojis={playerEmojis} seasonConfig={seasonConfig} />}
      {page === "lootbox" && <LootboxPage user={currentUser} userData={userData} onUserDataChange={() => loadUserData(currentUser)} seasonConfig={seasonConfig} />}
      {page === "marketplace" && <MarketplacePage user={currentUser} userData={userData} onUserDataChange={() => loadUserData(currentUser)} onNotificationCreated={() => loadNotifications(currentUser?.uid)} seasonConfig={seasonConfig} />}
      {page === "casino" && <CasinoPage user={currentUser} userData={userData} onUserDataChange={() => loadUserData(currentUser)} onTokenAdjustmentChange={setTokenAdjustment} isMobile={isMobile} />}
      {page === "stocks" && <div className="v2-page"><StocksTab user={currentUser} userData={userData} onUserDataChange={() => loadUserData(currentUser)} /></div>}
      {page === "stats" && <StatsPage players={players} user={currentUser} isMobile={isMobile} seasonConfig={seasonConfig} />}
      {page === "pass" && <PassPage user={currentUser} userData={userData} onUserDataChange={() => loadUserData(currentUser)} isMobile={isMobile} players={players} seasonConfig={seasonConfig} />}
      {page === "admin" && (
        <AdminPage players={players} onPlayersChange={loadPlayers} isAdmin={isAdmin} seasonConfig={seasonConfig} onSeasonConfigChange={setSeasonConfig} />
      )}
      {page === "settings" && <SettingsPage user={currentUser} userData={userData} onIgnSave={async (ign) => {
        await updateUserIgn(currentUser.uid, ign);
        toast.success('IGN updated!');
        loadUserData(currentUser);
      }} onLogout={handleLogout} />}
      {page === "download" && <DownloadPage />}
    </ErrorBoundary>
  );

  return (

    <div className={`app v2-app ${isMobile ? 'mobile' : ''}`}>
      <div className="v2-app__bg" />
      {isMobile ? (
        <>
          <MobileNav {...shellProps} />
          <main className="v2-app__main main-content">
            {pageContent}
          </main>
        </>
      ) : (
        <div className="v2-app__body">
          <Sidebar {...shellProps} />
          <main className="v2-app__main">
            <div className="v2-app__inner">
              {pageContent}
            </div>
          </main>
        </div>
      )}
      {showLoginModal && (
        <LoginModal
          onClose={() => {
            setShowLoginModal(false);
            if (!isAdmin) setPage("leaderboard");
          }}
          onLogin={async () => {
            // User data will be loaded by onAuthStateChanged
          }}
        />
      )}

      {/* IGN Modal */}
      {showIgnModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--bg-elevated)',
            padding: 'clamp(20px, 3vw, 32px)',
            borderRadius: '16px',
            maxWidth: 'min(500px, 90vw)',
            width: '100%',
            border: '2px solid var(--red)'
          }}>
            <h2 style={{ fontSize: 'clamp(18px, 2.5vw, 24px)', marginBottom: '16px', color: 'var(--red)' }}>
              🎮 Enter Your Among Us IGN
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', lineHeight: '1.6' }}>
              Please enter your <strong>In-Game Name (IGN)</strong> from Among Us.<br/>
              <span style={{ color: 'var(--red)', fontWeight: '600' }}>
                ⚠️ This MUST match EXACTLY (case-sensitive)!
              </span>
            </p>
            <input
              type="text"
              value={ignInput}
              onChange={(e) => setIgnInput(e.target.value)}
              placeholder="Enter your IGN..."
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '16px',
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                color: 'var(--text-primary)',
                marginBottom: '24px'
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleSaveIgn();
              }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleSaveIgn}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'var(--red)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Save IGN
              </button>
              {userData && userData.ign && (
                <button
                  onClick={() => setShowIgnModal(false)}
                  style={{
                    padding: '12px 24px',
                    background: 'transparent',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    color: 'var(--text-secondary)',
                    fontSize: '16px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>

  );
}
